"use client";

import {
  type AppLocale,
  type FunnelWizardState,
} from "@/lib/funnel-planner/schema";
import {
  encodePlannerShareState,
  createShareablePlannerState,
  normalizePlannerNodeId,
  normalizePlannerStep,
  parsePlannerShareParams,
  SHARE_PARAM,
  STEP_PARAM,
  NODE_PARAM,
  type PlannerDeepLink,
  type SharedPlannerStateResult,
} from "@/lib/funnel-planner/share-codec";

export {
  createShareablePlannerState,
  encodePlannerShareState,
  type PlannerDeepLink,
  type SharedPlannerStateResult,
} from "@/lib/funnel-planner/share-codec";

export function getStepFromPlannerUrl(search: string): FunnelWizardState["step"] | null {
  return normalizePlannerStep(new URLSearchParams(search).get(STEP_PARAM));
}

export function getNodeFromPlannerUrl(search: string): string | null {
  return normalizePlannerNodeId(new URLSearchParams(search).get(NODE_PARAM));
}

export function getPlannerDeepLinkFromUrl(search: string): PlannerDeepLink {
  return {
    step: getStepFromPlannerUrl(search),
    nodeId: getNodeFromPlannerUrl(search),
  };
}

export function parseSharedPlannerState(search: string): SharedPlannerStateResult {
  return parsePlannerShareParams(new URLSearchParams(search));
}

export function buildPlannerShareUrl(
  state: FunnelWizardState,
  nodeId?: string | null,
  baseHref?: string,
): string {
  const url = new URL(baseHref ?? window.location.href, window.location.href);
  // Shared URLs contain only planner-owned parameters, never incoming identity,
  // campaign, or session tokens from the current address.
  url.search = "";
  url.hash = "";
  url.searchParams.set(STEP_PARAM, String(state.step));
  const safeNodeId = normalizePlannerNodeId(nodeId ?? null);
  if (safeNodeId) url.searchParams.set(NODE_PARAM, safeNodeId);
  else url.searchParams.delete(NODE_PARAM);
  url.searchParams.set(SHARE_PARAM, encodePlannerShareState(state));
  return url.toString();
}

export async function createPlannerShortShareUrl(
  state: FunnelWizardState,
  locale: AppLocale,
  nodeId?: string | null,
  baseHref?: string,
  signal?: AbortSignal,
): Promise<string | null> {
  try {
    signal?.throwIfAborted();
    const response = await fetch("/api/funnel-planner/share", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "same-origin",
      cache: "no-store",
      signal,
      body: JSON.stringify({
        // v7 session-only fields never reach the share API or a stored plan.
        state: createShareablePlannerState(state),
        locale,
        nodeId: normalizePlannerNodeId(nodeId ?? null),
      }),
    });

    if (!response.ok) {
      return null;
    }

    const payload = await response.json() as { url?: unknown; path?: unknown };
    // Prefer the same-origin path: the absolute url reflects the server's own view of its origin (for example localhost
    // behind a 127.0.0.1 or proxy alias), which the origin check below would reject.
    const candidate = typeof payload.path === "string" && payload.path.length > 0 ? payload.path : payload.url;
    if (typeof candidate === "string" && candidate.length > 0) {
      const url = new URL(candidate, baseHref ?? window.location.href);
      if (url.origin !== window.location.origin || url.username || url.password || !/^https?:$/.test(url.protocol)) return null;
      return url.toString();
    }

    return null;
  } catch {
    signal?.throwIfAborted();
    return null;
  }
}

export function buildPlannerStageShareUrl(state: FunnelWizardState, nodeId: string): string {
  return buildPlannerShareUrl({ ...state, step: 5 }, nodeId);
}

export function buildPlannerStepUrl(step: FunnelWizardState["step"], nodeId?: string | null): string {
  const url = new URL(window.location.href);
  url.searchParams.set(STEP_PARAM, String(step));
  const safeNodeId = normalizePlannerNodeId(nodeId ?? null);
  if (safeNodeId) url.searchParams.set(NODE_PARAM, safeNodeId);
  else url.searchParams.delete(NODE_PARAM);
  url.searchParams.delete(SHARE_PARAM);
  return url.toString();
}

export function replacePlannerStepUrl(step: FunnelWizardState["step"], nodeId?: string | null): void {
  const url = new URL(window.location.href);
  url.searchParams.set(STEP_PARAM, String(step));
  const safeNodeId = normalizePlannerNodeId(nodeId ?? null);
  if (safeNodeId) url.searchParams.set(NODE_PARAM, safeNodeId);
  else url.searchParams.delete(NODE_PARAM);
  url.searchParams.delete(SHARE_PARAM);
  window.history.replaceState(window.history.state, "", url);
}
